import { FC, useContext, useMemo } from 'react';

import { useSession } from '@eneco/auth';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Form, PageGrid, Stack } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { PriceSimulationRendering } from '@sitecore/types/PriceSimulation';
import { useRouter } from 'next/router';
import { FormProvider, useForm } from 'react-hook-form';

import { PriceSimulationContext, PriceSimulationProvider } from './context/PriceSimulationProvider';
import PriceSimulationStepperFlow from './flows/PriceSimulationStepperFlow';
import {
  METER_TYPE_OPTIONS,
  PriceSimulationFormSchema,
  priceSimulationFormSchema,
} from './schemas/priceSimulationFormSchema';
import { buildDynamicStepOrder, StepBuilderParams } from './types/PriceSimulationSteps';
import { loadPriceSimulationState } from './utils/priceSimulationLocalStorage';
import FlowNavigationLayout from '../../components/navigation/flow/FlowNavigationLayout';
import { CUSTOMER_TYPE_PARAM } from '../../utils/addSearchParams';

const PriceSimulation: FC = () => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { query } = useRouter();
  const { data: session } = useSession();
  const customerType = query[CUSTOMER_TYPE_PARAM] as string;
  // Load saved state from cookie on mount
  const savedState = loadPriceSimulationState();

  const defaultValues: Partial<PriceSimulationFormSchema> = useMemo(
    () => ({
      isProspect: true,
      isUserDomiciledOnPostalCode: true,
      customerType: customerType === 'SOHO' || customerType === 'Residential' ? customerType : undefined,
      postalCode: '',
      city: '',
      hasExclusiveNightMeter: false,
      meterSubTypeRegister: 'Single',
      solarPanelInstallDate: 'BeforeJanuary2024',
      currentEnergyConsumptionQuestion: true,
      energyType: 'Both',
      electricityConsumption: {},
      meterType: 'digitalMeter',
      solarConsumptionChoice: 'kwh',
      ...savedState?.formValues,
    }),
    [customerType, savedState?.formValues],
  );

  // Calculate initial dynamic step order based on default values
  const initialStepBuilderParams: StepBuilderParams = useMemo(
    () => ({
      isLoggedIn: !!session,
      isProspect: defaultValues.isProspect ?? false,
      isSoho: defaultValues.customerType === 'SOHO',
      isConsumptionKnown: defaultValues.currentEnergyConsumptionQuestion ?? false,
      isAllowedConsumptionEstimationFlag: fields.dynamicProductFlow.allowConsumptionEstimationFlag.value ?? false,
      isGas: defaultValues.energyType === 'Gas' || defaultValues.energyType === 'Both',
      isElectricity: defaultValues.energyType === 'Electricity' || defaultValues.energyType === 'Both',
      hasSolarPanels: defaultValues.installationCheck?.includes('SolarPanels') ?? false,
      isRegionWalloon: defaultValues.regionInBelgium === 'Walloon',
      meterType: defaultValues.meterType ?? METER_TYPE_OPTIONS.DIGITAL,
    }),
    [session, defaultValues, fields.dynamicProductFlow.allowConsumptionEstimationFlag.value],
  );

  const initialDynamicStepOrder = useMemo(
    () => buildDynamicStepOrder(initialStepBuilderParams),
    [initialStepBuilderParams],
  );

  const form = useForm<PriceSimulationFormSchema>({
    resolver: typedYupResolver<PriceSimulationFormSchema>(priceSimulationFormSchema(fields, initialDynamicStepOrder)),
    defaultValues,
  });

  if (Object.keys(form.formState.errors).length > 0) console.warn(form.formState.errors);

  return (
    <FormProvider {...form}>
      <PriceSimulationProvider>
        <FlowNavigationLayout>
          <PriceSimulationContent />
        </FlowNavigationLayout>
      </PriceSimulationProvider>
    </FormProvider>
  );
};

const PriceSimulationContent = () => {
  const { fields } = useContent<PriceSimulationRendering>();
  const { priceSimulationStep } = useContext(PriceSimulationContext);

  if (priceSimulationStep === 'priceSimulationResult') {
    return (
      <PageGrid>
        <PageGrid.Item gridColumn={'-1/1'}>
          <Box paddingY="12">
            <Stack gap="10" alignX="start">
              <PriceSimulationStepperFlow fields={fields} />
            </Stack>
          </Box>
        </PageGrid.Item>
      </PageGrid>
    );
  }

  return (
    <Form>
      <PageGrid>
        <PageGrid.Item gridColumn={{ initial: '-1/1', md: '2/12', lg: '4/10' }}>
          <Box paddingY="12">
            <Stack gap="10" alignX="start">
              <PriceSimulationStepperFlow fields={fields} />
            </Stack>
          </Box>
        </PageGrid.Item>
      </PageGrid>
    </Form>
  );
};

export default PriceSimulation;
